/**
 * @file map.service.js
 * @description Pure helpers for the supplier destination map (coverage areas + reservations).
 * Nothing here depends on Google Maps, so it can be reused and reasoned about in isolation.
 */

import moment from "moment";
import { ROLES } from "../../../helpers/commons.helpers";

/* -------------------------------------------------------------------------- */
/* Constants                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Fixed palette used to colour coverage areas. The area index cycles through it.
 * @type {ReadonlyArray<string>}
 */
export const AREA_COLORS = Object.freeze([
  "#1f77b4",
  "#ff7f0e",
  "#2ca02c",
  "#d62728",
  "#9467bd",
  "#8c564b",
  "#e377c2",
  "#7f7f7f",
  "#bcbd22",
  "#17becf",
  "#3949ab",
  "#00897b"
]);

/**
 * Roles allowed to open the destination map. Administrative only, because it shows net prices.
 * @type {ReadonlyArray<string>}
 */
export const DESTINATION_MAP_ROLES = Object.freeze([
  ROLES.SUPER_ADMIN,
  ROLES.ADMIN,
  ROLES.MANAGER
]);

/** Default number of days back shown on the map. */
export const DEFAULT_MAP_RANGE_DAYS = 90;

/** Minimum number of points a polygon needs to be drawn. */
export const MIN_POLYGON_POINTS = 3;

/** Colour of the reservation price tags (RhomTrip $primary from the rhomtrip_assets theme). */
export const PRICE_TAG_COLOR = "#414f9f";

/** Colour of the reservation destination pin. */
export const DESTINATION_PIN_COLOR = "#2dce89";

/** Colour of the line between origin and destination. */
export const ROUTE_LINE_COLOR = "#5e72e4";

/**
 * Dark label style for InfoWindow fields (theme $black).
 * Inline because InfoWindows are rendered outside the component scoped styles.
 */
export const INFO_LABEL_STYLE = "font-weight:700;color:#1d1929;";

/** Geometry of the price tag icon, in pixels. */
const PRICE_TAG = Object.freeze({
  boxHeight: 22,
  pointer: 6,
  radius: 3,
  minWidth: 40,
  padding: 16,
  charWidth: 7.2,
  textBaseline: 15.5
});

/* -------------------------------------------------------------------------- */
/* Areas and coordinates                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Get the colour of an area by its position in the list.
 * @param {number} index - Area index.
 * @returns {string} Hex colour.
 */
export function getAreaColor(index) {
  return AREA_COLORS[index % AREA_COLORS.length];
}

/**
 * Check that a point has usable coordinates (not null and not the 0,0 default).
 * @param {{lat: number, lng: number}} point - Coordinates.
 * @returns {boolean}
 */
export function hasValidCoordinates(point) {
  if (!point) return false;
  const lat = Number(point.lat);
  const lng = Number(point.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  return !(lat === 0 && lng === 0);
}

/**
 * Get the valid polygon points of an area as plain {lat, lng} literals.
 * @param {Object} area - Area with a `polygon` array.
 * @returns {Array<{lat: number, lng: number}>}
 */
export function getAreaPath(area) {
  return (area.polygon || [])
    .filter(hasValidCoordinates)
    .map(point => ({ lat: point.lat, lng: point.lng }));
}

/**
 * Get the origin and destination of a reservation, each one only when it has valid coordinates.
 * @param {Object} reservation - Reservation with `locations.from` / `locations.to`.
 * @returns {{from: ?{lat: number, lng: number}, to: ?{lat: number, lng: number}}}
 */
export function getReservationPoints(reservation) {
  const { from, to } = reservation.locations || {};
  return {
    from: hasValidCoordinates(from) ? { lat: from.lat, lng: from.lng } : null,
    to: hasValidCoordinates(to) ? { lat: to.lat, lng: to.lng } : null
  };
}

/* -------------------------------------------------------------------------- */
/* Dates                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Build the default flatpickr range value (last N days until today).
 * @param {number} days - Days back.
 * @returns {string} "YYYY-MM-DD to YYYY-MM-DD"
 */
export function getDefaultDateRange(days = DEFAULT_MAP_RANGE_DAYS) {
  const end = moment().format("YYYY-MM-DD");
  const start = moment()
    .subtract(days, "days")
    .format("YYYY-MM-DD");
  return `${start} to ${end}`;
}

/**
 * Convert a flatpickr range value into the backend `date` param.
 * @param {string} range - "YYYY-MM-DD to YYYY-MM-DD" or "YYYY-MM-DD".
 * @returns {string} "YYYY-MM-DDtoYYYY-MM-DD", "YYYY-MM-DD" or "".
 */
export function toDateQuery(range) {
  return range ? range.replace(/\s/g, "") : "";
}

/* -------------------------------------------------------------------------- */
/* Prices                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Format an amount with two decimals.
 * @param {number} amount
 * @returns {string}
 */
function formatAmount(amount) {
  return Number(amount || 0).toFixed(2);
}

/**
 * Full quoted price (symbol + amount + currency code), used in the InfoWindow.
 * @param {{amount: number, currency: ?{code: string, symbol: string}}} quotedPrice
 * @returns {string} e.g. "€132.00 EUR"
 */
export function formatQuotedPrice(quotedPrice) {
  if (!quotedPrice) return "-";
  const { currency } = quotedPrice;
  const amount = formatAmount(quotedPrice.amount);
  if (!currency) return amount;
  return `${currency.symbol || ""}${amount} ${currency.code || ""}`.trim();
}

/**
 * Short quoted price (symbol + amount), used on the map tag.
 * @param {{amount: number, currency: ?{code: string, symbol: string}}} quotedPrice
 * @returns {string} e.g. "€132.00"
 */
export function formatShortPrice(quotedPrice) {
  if (!quotedPrice) return "-";
  const symbol = quotedPrice.currency ? quotedPrice.currency.symbol || "" : "";
  return `${symbol}${formatAmount(quotedPrice.amount)}`;
}

/* -------------------------------------------------------------------------- */
/* Price tag icon                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Compute the price tag size so the text always fits.
 * @param {string} text - Text shown inside the tag.
 * @returns {{width: number, height: number}}
 */
function getPriceTagSize(text) {
  const width = Math.max(
    PRICE_TAG.minWidth,
    Math.ceil(text.length * PRICE_TAG.charWidth) + PRICE_TAG.padding
  );
  return { width: width, height: PRICE_TAG.boxHeight + PRICE_TAG.pointer };
}

/**
 * Build the SVG path of a rounded box with a pointer centred at the bottom.
 * @param {number} width - Tag width.
 * @param {number} height - Tag height (box + pointer).
 * @returns {string} SVG path `d` attribute.
 */
function buildPriceTagPath(width, height) {
  const { boxHeight, pointer, radius } = PRICE_TAG;
  const left = 1;
  const right = width - 1;
  const top = 1;
  const middle = width / 2;
  return [
    `M${left + radius} ${top}`,
    `H${right - radius} Q${right} ${top} ${right} ${top + radius}`,
    `V${boxHeight - radius} Q${right} ${boxHeight} ${right - radius} ${boxHeight}`,
    `H${middle + pointer} L${middle} ${height - 1} L${middle - pointer} ${boxHeight}`,
    `H${left + radius} Q${left} ${boxHeight} ${left} ${boxHeight - radius}`,
    `V${top + radius} Q${left} ${top} ${left + radius} ${top} Z`
  ].join(" ");
}

/**
 * Build the SVG markup of the price tag.
 * @param {string} text - Text shown inside the tag.
 * @param {{width: number, height: number}} size - Tag size.
 * @returns {string} SVG markup.
 */
function buildPriceTagSvg(text, { width, height }) {
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`,
    `<path d="${buildPriceTagPath(
      width,
      height
    )}" fill="${PRICE_TAG_COLOR}" stroke="#ffffff" stroke-width="1"/>`,
    `<text x="${width / 2}" y="${PRICE_TAG.textBaseline}" text-anchor="middle" font-family="Arial, sans-serif" font-size="12" font-weight="bold" fill="#ffffff">${escapeHtml(
      text
    )}</text>`,
    `</svg>`
  ].join("");
}

/**
 * Build a price tag icon. The anchor is the tip of the pointer.
 * @param {string} text - Text shown inside the tag.
 * @returns {{url: string, width: number, height: number, anchorX: number, anchorY: number}}
 */
export function buildPriceTagIcon(text) {
  const size = getPriceTagSize(text);
  const svg = buildPriceTagSvg(text, size);
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    width: size.width,
    height: size.height,
    anchorX: size.width / 2,
    anchorY: size.height
  };
}

/* -------------------------------------------------------------------------- */
/* InfoWindow content                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Escape text before injecting it into HTML or SVG.
 * @param {*} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value === null || value === undefined ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Build one "Label: value" line of the InfoWindow.
 * @param {string} label - Field label.
 * @param {*} value - Field value (escaped).
 * @returns {string} HTML string.
 */
function buildInfoField(label, value) {
  return `<div><b style="${INFO_LABEL_STYLE}">${escapeHtml(
    label
  )}:</b> ${escapeHtml(value)}</div>`;
}

/**
 * Build the link to the reservation detail page.
 * @param {string} reservationId - Reservation id.
 * @returns {string} HTML string.
 */
function buildReservationLink(reservationId) {
  return `<a href="/reservations/${escapeHtml(
    reservationId
  )}" target="_blank" rel="noopener">Open reservation</a>`;
}

/**
 * Build the InfoWindow content of a reservation.
 * The booking number goes in `title` so it can be used as the InfoWindow header.
 * @param {Object} reservation - Reservation returned by the branch map endpoint.
 * @returns {{title: string, body: string}} Plain title and HTML body.
 */
export function buildReservationInfoContent(reservation) {
  const body = [
    buildInfoField("From", reservation.from),
    buildInfoField("To", reservation.to),
    buildInfoField("Quoted price", formatQuotedPrice(reservation.quotedPrice)),
    buildReservationLink(reservation._id)
  ].join("");
  return {
    title: reservation.booking_number || "",
    body: `<div class="map-info">${body}</div>`
  };
}

/**
 * Build the InfoWindow content of a coverage area.
 * @param {Object} area - Area with code and description.
 * @returns {{title: string, body: string}} Plain title and HTML body.
 */
export function buildAreaInfoContent(area) {
  return {
    title: area.code || "",
    body: `<div class="map-info">${escapeHtml(area.description)}</div>`
  };
}
